import {
  openConfirmDialog,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {match} from 'ts-pattern';

import {always, head, isNil} from 'ramda';
import {isArray, isNilOrEmpty, isString} from 'ramda-adjunct';

import {
  ModuleSourceEnum,
  useDeleteInvoiceDraftMutation,
  useSendCorrectiveTaxDocumentMutation,
  useSendInvoiceMutation,
} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {accountingRoutes, testIds} from '@dms/routes';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {InvoicePaymentForm} from '../components/InvoicePaymentForm/InvoicePaymentForm';
import {InvoiceTypes} from '../types/InvoiceTypes';
import {handleApiError} from '../utils/handleApiError';
import {useCancelAccountingDocument} from './useCancelAccountingDocument';
import {useDownloadAccountingDocument} from './useDownloadAccountingDocument';
import {useRedirectToInvoice} from './useRedirectToInvoice';

export const INVOICE_PREDEFINED_COLUMNS = [
  'invoiceType',
  'number',
  'moduleSource',
  'invoiceCategory',
] as const;

export const useInvoiceActionCallback = () => {
  const navigate = useNavigate();
  const [redirectToInvoice] = useRedirectToInvoice();
  const [downloadAccountingInvoice] = useDownloadAccountingDocument();
  const [downloadAccountingInvoiceAsDocx] = useDownloadAccountingDocument('downloadDocx');
  const [deleteDraftInvoice] = useDeleteInvoiceDraftMutation();
  const [cancelDocument] = useCancelAccountingDocument();
  const [sendInvoice] = useSendInvoiceMutation();
  const [sendCorrectiveTaxDocument] = useSendCorrectiveTaxDocumentMutation();

  const handleDeleteDraftInvoice = async (invoiceDraftId: string) =>
    await deleteDraftInvoice({invoiceDraftId}).unwrap().catch(handleApiError);

  const redirectToCreateCorrectiveTaxDocument = (
    invoiceType: InvoiceTypes | undefined,
    id: string
  ) => {
    if (isNil(invoiceType)) {
      showNotification.error();
      return;
    }
    navigate(
      composePath(accountingRoutes.newCorrectiveTaxDocument, {
        params: {id},
        queryParams: {invoiceType},
      })
    );
  };

  const handleSendInvoice = async (id: string, invoiceType: string | undefined) => {
    if (isNilOrEmpty(invoiceType)) {
      showNotification.error();
      return;
    }

    const cancelAction =
      invoiceType === 'CORRECTIVE_TAX_DOCUMENT'
        ? sendCorrectiveTaxDocument({correctiveTaxDocumentId: id})
        : sendInvoice({invoiceId: id});

    await cancelAction
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.invoice.state.sent')))
      .catch(handleApiError);
  };

  const actionCallback: ActionCallback<typeof INVOICE_PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
  }) => {
    const id = isArray(rowId) ? head(rowId) : rowId;

    const moduleSource = getColumnData<{key: ModuleSourceEnum}>('moduleSource')?.value?.key;

    if (isNil(rowId) || !isString(id)) {
      showNotification.error();
      return;
    }

    const invoiceType = getColumnData<{key: InvoiceTypes}>('invoiceType')?.value?.key;
    const invoiceNumber = getColumnData<string>('number')?.value;

    if (isNil(invoiceType)) {
      showNotification.error();
      return;
    }

    const invoiceCategory = getColumnData<{key: 'default' | 'internal'}>('invoiceCategory')?.value
      ?.key;

    const paymentId = match(invoiceType)
      .with('CORRECTIVE_TAX_DOCUMENT', always({correctiveTaxDocumentId: id}))
      .with('PROFORMA', always({proformaId: id}))
      .otherwise(always({invoiceId: id}));

    match(actionKey)
      .with('detail', () => redirectToInvoice(invoiceType, id))
      .with('download', () =>
        downloadAccountingInvoice(invoiceType, id, moduleSource, invoiceNumber, invoiceCategory)
      )
      .with('download_docx', () =>
        downloadAccountingInvoiceAsDocx(
          invoiceType,
          id,
          moduleSource,
          invoiceNumber,
          invoiceCategory
        )
      )
      .with('create_corrective_tax_document', () =>
        openConfirmDialog({
          onConfirm: () => redirectToCreateCorrectiveTaxDocument(invoiceType, id),
          text: i18n.t('entity.invoice.notifications.generateCorrectiveTaxDocumentHelperText', {
            invoiceNumber,
          }),
        })
      )
      .with('send', () =>
        openConfirmDialog({
          onConfirm: () => handleSendInvoice(id, invoiceType).then(() => refreshData()),
          text: i18n.t('entity.invoice.notifications.sendInvoiceHelperText'),
        })
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () => handleDeleteDraftInvoice(id).then(() => refreshData()),
          text: i18n.t('entity.invoice.notifications.deleteDraftInvoiceHelperText'),
        })
      )
      .with('cancel', () =>
        openConfirmDialog({
          onConfirm: () => cancelDocument(invoiceType, id).then(() => refreshData()),
          text: i18n.t('page.accounting.invoiceDetail.cancelDialogText', {
            invoiceNumber,
          }),
        })
      )
      .with('pay', () =>
        openDialog(
          <InvoicePaymentForm
            {...paymentId}
            onAfterSubmit={refreshData}
            data-testid={testIds.accounting.invoiceOverview('paymentDialog')}
          />,
          {
            title: i18n.t('entity.invoice.labels.paymentOfInvoice', {
              number: invoiceNumber,
            }),
            'data-testid': testIds.accounting.invoiceOverview('paymentDialog'),
            scrollBehavior: 'outside',
          }
        )
      )
      .otherwise(showNotification.error);
  };

  return actionCallback;
};
